// Phase 10 — useAcademy hook.
//
// Read-only consumer of academy_courses, academy_lessons,
// and academy_progress tables.

import { useEffect, useMemo, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";

// ─── Types ─────────────────────────────────────────────────────

export interface AcademyCourse {
  id: string;
  course_key: string;
  title: string;
  description: string | null;
  category: string;
  lesson_count: number;
  estimated_mins: number;
  difficulty: string;
  sort_order: number;
  published: boolean;
  created_at: string;
}

export interface UseAcademyState {
  courses: readonly AcademyCourse[];
  loading: boolean;
  error: string | null;
}

// ─── Parser ────────────────────────────────────────────────────

function asStr(v: unknown): string | null {
  return typeof v === "string" && v.length > 0 ? v : null;
}

function asNum(v: unknown): number {
  if (v === null || v === undefined) return 0;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
}

function parseCourse(row: Record<string, unknown>): AcademyCourse {
  return {
    id: String(row.id ?? ""),
    course_key: String(row.course_key ?? ""),
    title: String(row.title ?? ""),
    description: asStr(row.description),
    category: String(row.category ?? "foundations"),
    lesson_count: asNum(row.lesson_count),
    estimated_mins: asNum(row.estimated_mins),
    difficulty: String(row.difficulty ?? "beginner"),
    sort_order: asNum(row.sort_order),
    published: row.published !== false,
    created_at: String(row.created_at ?? ""),
  };
}

// ─── Hook ──────────────────────────────────────────────────────

export function useAcademy(): UseAcademyState {
  const { user } = useAuthSession();
  const [courses, setCourses] = useState<readonly AcademyCourse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      setCourses([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const client = getBrowserClient();
    setLoading(true);
    setError(null);

    void (async () => {
      const resp = await (
        client.from("academy_courses" as never) as unknown as {
          select: (cols: string) => {
            order: (col: string, opts: { ascending: boolean }) => Promise<{
              data: Record<string, unknown>[] | null;
              error: { message: string } | null;
            }>;
          };
        }
      )
        .select("*")
        .order("sort_order", { ascending: true });

      if (cancelled) return;

      if (resp.error) {
        setError(resp.error.message);
        setCourses([]);
        setLoading(false);
        return;
      }

      setCourses(
        (resp.data ?? [])
          .filter((r): r is Record<string, unknown> => r !== null)
          .map(parseCourse),
      );
      setLoading(false);
    })();

    return () => { cancelled = true; };
  }, [user]);

  return useMemo(
    () => ({ courses, loading, error }),
    [courses, loading, error],
  );
}

// ─── Static course definitions ────────────────────────────────
// The 8 STRIKZA Academy courses per master build spec.
// Used as fallback display when no DB records exist.

export const ACADEMY_COURSES: {
  key: string;
  title: string;
  description: string;
  category: string;
  lessons: number;
  mins: number;
  difficulty: string;
}[] = [
  {
    key: "probability_thinking",
    title: "Probability Thinking",
    description: "Foundation course on expected value, variance, and probabilistic reasoning for informed decision-making.",
    category: "Foundations",
    lessons: 6,
    mins: 45,
    difficulty: "beginner",
  },
  {
    key: "clv_fundamentals",
    title: "Customer Lifetime Value",
    description: "Understanding CLV as the core metric for long-term sustainable value creation across all markets.",
    category: "Foundations",
    lessons: 5,
    mins: 35,
    difficulty: "beginner",
  },
  {
    key: "kelly_phase_withdrawal",
    title: "Kelly Criterion & Phase Withdrawal",
    description: "Position sizing using the Kelly fraction and structured withdrawal strategies for capital protection.",
    category: "Risk Management",
    lessons: 8,
    mins: 60,
    difficulty: "intermediate",
  },
  {
    key: "forex_2pct_rule",
    title: "Forex 2% Rule",
    description: "Never risk more than 2% of capital on a single position. Discipline-first approach to forex markets.",
    category: "Risk Management",
    lessons: 5,
    mins: 30,
    difficulty: "intermediate",
  },
  {
    key: "crypto_position_sizing",
    title: "Crypto Position Sizing",
    description: "Volatility-adjusted position sizing for cryptocurrency markets. Managing asymmetric risk profiles.",
    category: "Risk Management",
    lessons: 6,
    mins: 40,
    difficulty: "advanced",
  },
  {
    key: "reading_signal_card",
    title: "Reading the Signal Card",
    description: "How to interpret STRIKZA signal cards — confidence scores, edge values, market context, and quality indicators.",
    category: "Platform",
    lessons: 4,
    mins: 25,
    difficulty: "beginner",
  },
  {
    key: "rollover_system",
    title: "The ROLLOVER System",
    description: "Deep dive into multi-leg chain strategies, compounding mechanics, and risk-controlled accumulation.",
    category: "Platform",
    lessons: 7,
    mins: 50,
    difficulty: "intermediate",
  },
  {
    key: "responsible_wealth",
    title: "Responsible Wealth Building",
    description: "Discipline, emotional control, bankroll management, and the psychology of long-term wealth creation.",
    category: "Foundations",
    lessons: 6,
    mins: 40,
    difficulty: "beginner",
  },
];
